import { useEffect, useState, useRef } from "react";
import { Form } from "react-bootstrap";
import {
    Material, MaterialKind, Interaction, ActivityType,
    PRETASK_CATEGORIES, POSTASK_CATEGORIES, ACTIVITY_TYPES_V1, TASK_MODEL_ALLOWED_TYPES
} from "../../interfaces/Task";
import { Media } from "../../interfaces/Media";
import Uploader from "../../elements/Uploader";
import CustomEditor from "../../elements/CustomEditor";
import { Overlay } from "../../elements/Overlay";
import EditInteraction from "./EditInteraction";
import EditExercise from "./EditExercise";
import ConfirmationDialog from "../../elements/ConfirmationDialog";
import { registerEventEnd, registerEventStart } from "../../../utils/utils";
import { handleApiError, safeJsonParse, showErrorNotification } from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import AITools from "../../elements/AITools";
import { useTranslation } from "react-i18next";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

/**
 * UnifiedTaskEditor — editor únic de pre/post-tasks (format nou).
 *
 * Substitueix els ~44 components New i Edit per tipus. Drop-in: rep saveForm /
 * closeForm com els antics. Quan rep `id`, carrega l'ítem (el backend el
 * retorna ja en format nou via conversió al vol) i el desa en format nou.
 *
 * Veure taskgen-srv/docs/Disseny-PrePostTasks.md.
 */
export interface UnifiedTaskEditorProps {
    task_id: string;
    phase: 'pre' | 'post';
    id?: string;                  // editant un existent; absent = creant
    initialCategory?: string;     // categoria triada al picker de creació
    saveForm: CallableFunction;
    closeForm: CallableFunction;
    task_language?: string;
}

const emptyInteraction: Interaction = { groups: [], roles: [], prompts: [] };

/**
 * Elimina recursivament els `_id` amb string buit. Els subdocuments nous de la
 * interacció (grups, rols, prompts, estudiants) es creen amb `_id: ''`; Mongoose
 * no pot convertir '' a ObjectId i la desada sencera falla amb CastError. En
 * ometre el camp, Mongoose en genera un de nou. Els `_id` reals es conserven.
 */
function stripEmptyIds(value: any): any {
    if (Array.isArray(value)) return value.map(stripEmptyIds);
    if (value && typeof value === 'object' && !(value instanceof Date)) {
        const out: any = {};
        for (const [k, v] of Object.entries(value)) {
            if (k === '_id' && v === '') continue;
            out[k] = stripEmptyIds(v);
        }
        return out;
    }
    return value;
}

export default function UnifiedTaskEditor(props: UnifiedTaskEditorProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isPre = props.phase === 'pre';

    const [eventId, setEventId] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState<string>(props.initialCategory || (isPre ? PRETASK_CATEGORIES[0] : POSTASK_CATEGORIES[0]));
    const [types, setTypes] = useState<ActivityType[]>([]);
    const [materials, setMaterials] = useState<Material[]>([]);
    const [interaction, setInteraction] = useState<Interaction>(emptyInteraction);
    const [exerciseId, setExerciseId] = useState<string | null>(null);
    const [instructions, setInstructions] = useState('');
    const [teacherNotes, setTeacherNotes] = useState('');
    const [showInteractionEditor, setShowInteractionEditor] = useState(false);
    // Títol de la sub-vista del dissenyador (prompt/rol); null = dissenyador.
    const [interactionSubTitle, setInteractionSubTitle] = useState<string | null>(null);
    const exitInteraction = () => { setShowInteractionEditor(false); setInteractionSubTitle(null); };
    // Dissenyador d'exercicis (s'incrusta al mateix modal, com la interacció).
    const [showExerciseEditor, setShowExerciseEditor] = useState(false);

    // Claus estables per material (React + valor inicial del rich text editor).
    const keyCounter = useRef(0);
    const nextKey = () => 'm' + (keyCounter.current++);
    // Snapshot del contingut inicial de cada editor de text (CustomEditor és no
    // controlat: si li canvies `data` a cada tecla reseteja el cursor). Capturem
    // el valor inicial un cop per `_key` i el mantenim estable.
    const editorInit = useRef<Record<string, { data: string; formatted: string }>>({});
    const getEditorInit = (m: Material) => {
        const k = m._key || 'x';
        if (!editorInit.current[k]) {
            editorInit.current[k] = { data: m.text || '', formatted: m.formatted_text || '' };
        }
        return editorInit.current[k];
    };

    // Plegat/desplegat per material (plegats per defecte; absent = plegat).
    const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
    // Índex del material pendent de confirmar esborrat.
    const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);

    // Custom només apareix al selector si l'ítem ja és custom (§3.2).
    const officialCategories = isPre ? PRETASK_CATEGORIES : POSTASK_CATEGORIES;
    const categoryOptions = category === 'custom' ? [...officialCategories, 'custom'] : officialCategories;
    const allowedTypes: ActivityType[] = category === 'task_model' ? TASK_MODEL_ALLOWED_TYPES : ACTIVITY_TYPES_V1;

    useEffect(() => {
        if (props.id) {
            const endpoint = isPre ? 'pretask' : 'postask';
            fetch(import.meta.env.VITE_API_URL + '/v1/task/' + props.task_id + '/' + endpoint + '/' + props.id, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') }
            })
                .then(response => handleApiError(response, navigate) ? null : safeJsonParse(response))
                .then(data => {
                    if (!data) return;
                    setName(data.name || '');
                    setCategory(data.category || 'custom');
                    setTypes(Array.isArray(data.types) ? data.types : []);
                    setMaterials((Array.isArray(data.materials) ? data.materials : []).map((mm: Material) => ({ ...mm, _key: nextKey() })));
                    setInteraction(data.interaction || emptyInteraction);
                    setExerciseId(data.exerciseId || null);
                    setInstructions(data.instructions || '');
                    setTeacherNotes(data.teacher_notes || '');
                });
        }
        registerEventStart('edit ' + props.phase + 'task unified', props.task_id).then(setEventId);
    }, []);

    useEffect(() => () => { if (eventId) registerEventEnd(eventId); }, [eventId]);

    // ── Types ──
    const toggleType = (type: ActivityType) => {
        setTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    }

    // En canviar a Task model, retalla els types no permesos.
    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCat = e.target.value;
        setCategory(newCat);
        if (newCat === 'task_model') {
            setTypes(prev => prev.filter(t => TASK_MODEL_ALLOWED_TYPES.includes(t)));
        }
    }

    // ── Materials ──
    const addMaterial = (kind: MaterialKind) => {
        const _key = nextKey();
        const m: Material = kind === 'text'
            ? { kind, description: '', text: '', formatted_text: '', _key }
            : { kind, description: '', file: null, _key };
        setMaterials(prev => [...prev, m]);
        setExpandedKeys(prev => ({ ...prev, [_key]: true }));   // desplegat en afegir
    }

    const updateMaterial = (index: number, patch: Partial<Material>) => {
        setMaterials(prev => prev.map((m, i) => i === index ? { ...m, ...patch } : m));
    }

    const removeMaterial = (index: number) => {
        setMaterials(prev => prev.filter((_, i) => i !== index));
    }

    const toggleExpand = (key: string) => {
        setExpandedKeys(prev => {
            const nowExpanded = !prev[key];
            // En plegar, invalidem el snapshot inicial de l'editor perquè en
            // tornar a desplegar reculli el contingut actual del material.
            if (!nowExpanded) delete editorInit.current[key];
            return { ...prev, [key]: nowExpanded };
        });
    }

    const confirmDelete = () => {
        if (confirmDeleteIndex !== null) removeMaterial(confirmDeleteIndex);
        setConfirmDeleteIndex(null);
    }

    // Reordenació de materials (drag & drop amb react-dnd, vegeu MaterialRow).
    const moveMaterial = (from: number, to: number) => {
        if (from === to || from < 0 || to < 0) return;
        setMaterials(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    }

    // ── Save / Close ──
    const handleSave = () => {
        if (!name.trim()) {
            showErrorNotification(t("Please enter a title."));
            return;
        }
        if (types.length < 1) {
            showErrorNotification(t("Select at least one activity type"));
            return;
        }
        registerEventEnd(eventId);
        const payload = {
            _id: props.id || '',
            name,
            updated: new Date(),
            category,
            types,
            materials: materials.map(({ _key, ...rest }) => rest),
            interaction: types.includes('interaction') ? stripEmptyIds(interaction) : null,
            exerciseId: types.includes('exercise') ? exerciseId : null,
            instructions,
            teacher_notes: teacherNotes,
        };
        props.saveForm(payload);
    }

    const handleClose = () => {
        registerEventEnd(eventId);
        props.closeForm();
    }

    const doNothing = () => {};

    const kindLabel: Record<MaterialKind, string> = {
        text: t("Text"), image: t("Image"), audio: t("Audio"), video: t("Video")
    };

    return (
        // Un únic DndProvider per a tot l'editor: el comparteixen el drag&drop
        // de materials i el de preguntes del dissenyador d'exercicis. No es pot
        // muntar un segon HTML5Backend mentre el formulari (amb el seu) segueix
        // muntat ocult, així que es puja aquí.
        <DndProvider backend={ HTML5Backend }>
            <Overlay
                title={ showInteractionEditor
                    ? (interactionSubTitle || t("Edit interaction design"))
                    : showExerciseEditor
                        ? t("Edit exercise")
                        : (isPre ? t("Edit Pre-Task") : t("Edit Post-Task")) }
                onClose={ showInteractionEditor
                    ? exitInteraction
                    : showExerciseEditor
                        ? () => setShowExerciseEditor(false)
                        : handleClose }
                className={ 'new-task-container unified-task-editor' + (showInteractionEditor ? ' interaction-activity' : '') }
                actions={ (showInteractionEditor || showExerciseEditor)
                    ? undefined
                    : [
                        { label: t("Cancel"), variant: 'secondary', onClick: handleClose },
                        { label: t("Save"), variant: 'primary', onClick: handleSave }
                    ] }
            >
                { showInteractionEditor &&
                    <EditInteraction
                        embedded
                        onTitle={ setInteractionSubTitle }
                        taskId={ props.task_id }
                        task_language={ props.task_language }
                        interaction={ interaction }
                        saveCallback={ (newInteraction: Interaction) => setInteraction(newInteraction) }
                        closeCallback={ exitInteraction }
                    />
                }
                { showExerciseEditor &&
                    <EditExercise
                        embedded
                        task_id={ props.task_id }
                        task_language={ props.task_language }
                        exerciseId={ exerciseId }
                        onSaved={ (id: string) => setExerciseId(id) }
                        onClose={ () => setShowExerciseEditor(false) }
                    />
                }
                <div className="inner" style={ (showInteractionEditor || showExerciseEditor) ? { display: 'none' } : undefined }>
                    <Form className="unified-task-form">
                        <div className="fields-container two-col">
                            <div className="col-left">
                            <AITools />
                            <Form.Group className="field field-title">
                                <Form.Label>{ isPre ? t("Title of the Pre-Task") : t("Title of the Post-Task") }</Form.Label>
                                <Form.Control type="text" name="name" required value={ name }
                                    onChange={ e => setName(e.target.value) } />
                            </Form.Group>

                            <Form.Group className="field field-category">
                                <Form.Label>{ t("Type of pre/post-task") }</Form.Label>
                                <Form.Select value={ category } onChange={ handleCategoryChange }>
                                    { categoryOptions.map(c => (
                                        <option key={ c } value={ c }>{ t('category_' + c) }</option>
                                    )) }
                                </Form.Select>
                            </Form.Group>

                            <div className="field field-activity-types">
                                <div className="label">
                                    { t("Activity types") }
                                    { types.length < 1 &&
                                        <span className="hint">{ t("Select at least one activity type") }</span>
                                    }
                                </div>
                                <div className="activity-types-checkboxes">
                                    { allowedTypes.map(type => (
                                        <Form.Check key={ type } type="checkbox" id={ 'type-' + type }
                                            label={ <><span className={ 'activity-icon activity-icon-' + type } />{ t('activity_' + type) }</> }
                                            checked={ types.includes(type) }
                                            onChange={ () => toggleType(type) } />
                                    )) }
                                </div>
                            </div>

                            <div className="field field-instructions">
                                <CustomEditor change={ setInstructions } changeFormatted={ doNothing }
                                    data={ instructions } formatted={ '' } id="instructions"
                                    label={ t("Aim and instructions") } npl={ false } task_id={ '' } />
                            </div>

                            <div className="field field-teachernotes">
                                <CustomEditor change={ setTeacherNotes } changeFormatted={ doNothing }
                                    data={ teacherNotes } formatted={ '' } id="teachernotes"
                                    label={ t("Teacher notes") } npl={ false } task_id={ '' } />
                            </div>
                            </div>{/* /col-left */}

                            <div className="col-right">
                            {/* Materials ordenats */}
                            <div className="field field-materials">
                                <div className="label">{ t("Materials") }</div>
                                <div className="materials-add">
                                    <a className="material-add material-add-text" onClick={ () => addMaterial('text') }>{ t("Add text") }</a>
                                    <a className="material-add material-add-image" onClick={ () => addMaterial('image') }>{ t("Add image") }</a>
                                    <a className="material-add material-add-audio" onClick={ () => addMaterial('audio') }>{ t("Add audio") }</a>
                                    <a className="material-add material-add-video" onClick={ () => addMaterial('video') }>{ t("Add video") }</a>
                                </div>
                                <div className="materials-list">
                                    { materials.map((m, index) => (
                                        <MaterialRow
                                            key={ m._key || index }
                                            m={ m }
                                            index={ index }
                                            expanded={ !!expandedKeys[m._key || ''] }
                                            kindLabel={ kindLabel }
                                            getEditorInit={ getEditorInit }
                                            moveMaterial={ moveMaterial }
                                            onToggleExpand={ () => toggleExpand(m._key || '') }
                                            onUpdate={ updateMaterial }
                                            onRequestDelete={ setConfirmDeleteIndex }
                                            task_id={ props.task_id }
                                            task_language={ props.task_language } />
                                    )) }
                                </div>
                            </div>

                            {/* Singleton: Interaction */}
                            { types.includes('interaction') &&
                                <div className="field field-interaction">
                                    <div className="label">{ t("Interaction") }</div>
                                    <div className="interaction-summary">
                                        { t("Groups") }: { interaction.groups.length } · { t("Roles") }: { interaction.roles.length } · { t("Prompts") }: { interaction.prompts.length }
                                    </div>
                                    <button type="button" className="btn btn-secondary btn-sm"
                                        onClick={ () => setShowInteractionEditor(true) }>
                                        { t("Edit interaction design") }
                                    </button>
                                </div>
                            }

                            {/* Singleton: Exercise (dissenyador d'exercicis, Fase 6) */}
                            { types.includes('exercise') &&
                                <div className="field field-exercise">
                                    <div className="label">{ t("Exercise") }</div>
                                    <div className="exercise-summary">
                                        { exerciseId ? t("Exercise configured") : t("No exercise yet") }
                                    </div>
                                    <button type="button" className="btn btn-secondary btn-sm"
                                        onClick={ () => setShowExerciseEditor(true) }>
                                        { t("Edit exercise") }
                                    </button>
                                </div>
                            }
                            </div>{/* /col-right */}
                        </div>
                    </Form>
                </div>
            </Overlay>

            { confirmDeleteIndex !== null &&
                <ConfirmationDialog
                    message={ t("Are you sure you want to delete this material?") }
                    confirmLabel={ t("Delete") }
                    cancelLabel={ t("Cancel") }
                    confirmCallback={ confirmDelete }
                    cancelCallback={ () => setConfirmDeleteIndex(null) }
                />
            }
        </DndProvider>
    );
}

interface MaterialRowProps {
    m: Material;
    index: number;
    expanded: boolean;
    kindLabel: Record<MaterialKind, string>;
    getEditorInit: (m: Material) => { data: string; formatted: string };
    moveMaterial: (from: number, to: number) => void;
    onToggleExpand: () => void;
    onUpdate: (index: number, patch: Partial<Material>) => void;
    onRequestDelete: (index: number) => void;
    task_id: string;
    task_language?: string;
}

/**
 * MaterialRow — una targeta de material, arrossegable per reordenar (react-dnd,
 * mateix patró que DraggableTaskItem/SectionDropZone). Feedback visual:
 *   · isDragging → la targeta origen s'atenua (.dragging).
 *   · isOver     → línia d'inserció a dalt/baix segons la direcció d'arrossegament
 *                  (.drop-before / .drop-after), indicant on caurà.
 * Només el handle (⠿) és l'origen del drag; tota la targeta és zona de deixada.
 */
function MaterialRow(props: MaterialRowProps) {
    const { t } = useTranslation();
    const { m, index, expanded, kindLabel, getEditorInit, moveMaterial } = props;

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'material',
        item: { index },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
    }), [index]);

    const [{ isOver, draggedIndex }, drop] = useDrop(() => ({
        accept: 'material',
        drop: (item: { index: number }) => {
            if (item.index !== index) moveMaterial(item.index, index);
        },
        collect: monitor => ({
            isOver: monitor.isOver(),
            draggedIndex: (monitor.getItem() as { index: number } | null)?.index ?? null,
        }),
    }), [index, moveMaterial]);

    const showLine = isOver && draggedIndex !== null && draggedIndex !== index;
    const dropBefore = showLine && (draggedIndex as number) > index;   // arrossegant cap amunt
    const dropAfter = showLine && (draggedIndex as number) < index;    // arrossegant cap avall

    const className = 'material-item material-' + m.kind
        + (expanded ? ' expanded' : ' collapsed')
        + (isDragging ? ' dragging' : '')
        + (dropBefore ? ' drop-before' : '')
        + (dropAfter ? ' drop-after' : '');

    return (
        <div className={ className }
            ref={ (node: HTMLDivElement | null) => { drop(node); preview(node); } }>
            <div className="material-header">
                <span className="material-drag" ref={ drag as any }
                    title={ t("Drag to reorder") }>⠿</span>
                <a className="material-toggle" onClick={ props.onToggleExpand }>
                    <span className="material-chevron" />
                    <span className={ 'material-kind material-kind-' + m.kind }>
                        { m.description || kindLabel[m.kind] }
                    </span>
                </a>
                <div className="material-actions">
                    <button type="button" title={ t("Delete") } className="delete"
                        onClick={ () => props.onRequestDelete(index) }>✕</button>
                </div>
            </div>
            { expanded &&
                <div className="material-body">
                    { /* Títol del material (una línia), abans del contingut */ }
                    <input type="text" className="material-title" placeholder={ t("Title") }
                        value={ m.description || '' }
                        onChange={ e => props.onUpdate(index, { description: e.target.value }) } />
                    { m.kind === 'text' &&
                        <CustomEditor
                            change={ (v: string) => props.onUpdate(index, { text: v }) }
                            changeFormatted={ (v: string) => props.onUpdate(index, { formatted_text: v }) }
                            data={ getEditorInit(m).data }
                            formatted={ getEditorInit(m).formatted }
                            id={ 'material-text-' + (m._key || index) }
                            label={ t("Text") }
                            npl={ true }
                            task_id={ props.task_id }
                            task_language={ props.task_language } />
                    }
                    { m.kind !== 'text' &&
                        <Uploader media={ m.file ? [m.file] : [] }
                            save={ (arr: Media[]) => props.onUpdate(index, { file: arr.length ? arr[arr.length - 1] : null }) } />
                    }
                </div>
            }
        </div>
    );
}
